<ng-container *ngIf="canCreate">
  <div class="container">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="col-9">
        <h1 class="fs-25 fw-bold">Create a Microlearning Course</h1>
      </div>
      <div class="col-3 col-md-auto">
        <app-action-buttons />
      </div>
    </div>

    <div class="card border-0">
      <div class="card-body px-5 py-4">
        <ng-container *ngIf="errorMessage">
          <div
            class="alert alert-danger text-start d-flex justify-content-between mb-3"
            role="alert">
            {{ errorMessage }}
            <button
              type="button"
              class="btn-close"
              (click)="errorMessage = ''"
              data-bs-dismiss="alert"
              aria-label="Close"></button>
          </div>
        </ng-container>
        <form [formGroup]="form">
          <!-- CONTENT SCREEN -->
          <ng-container *ngIf="currentScreen === 'content'">
            <div
              class="d-flex justify-content-between position-relative align-items-end">
              <button
                *ngIf="usersDistrictHasSessions"
                type="button"
                class="btn btn-primary"
                (click)="handleBackNavigation()">
                <i class="bi bi-chevron-left"></i>
                Back
              </button>
              <h2
                class="fs-25 fw-bold mb-0 position-absolute bottom-0 start-50 translate-middle-x position-absolute bottom-0 start-50 translate-middle-x">
                Content
              </h2>
              <button
                [disabled]="contentSubmitLoading"
                type="button"
                class="btn btn-primary ps-3"
                (click)="navigateToQuiz()">
                <span
                  *ngIf="contentSubmitLoading"
                  class="spinner-border spinner-border-sm me-1"></span>
                Next: <b>Quiz</b> <i class="bi bi-chevron-right ms-1"></i>
              </button>
            </div>
            <hr class="border border-tertiary border-1 mb-4" />
            <div class="row flex-column flex-lg-row">
              <div class="col pe-lg-4">
                <label for="title" class="form-label">Title</label>
                <div class="input-group">
                  <input
                    formControlName="title"
                    type="text"
                    class="form-control"
                    id="title"
                    aria-label="title"
                    [ngClass]="{
                      'is-invalid': contentFormSubmitted && f['title'].errors
                    }" />
                </div>
                <label for="short-description" class="form-label"
                  >Short Description</label
                >
                <div class="input-group">
                  <textarea
                    formControlName="shortDescription"
                    class="form-control"
                    id="short-description"
                    aria-label="Short Description"
                    style="height: 120px"
                    [ngClass]="{
                      'is-invalid':
                        contentFormSubmitted && f['shortDescription'].errors
                    }"></textarea>
                </div>
              </div>
              <div class="col ps-lg-4">
                <div class="mb-3">
                  <label class="form-label">Image</label>
                  <ng-container *ngIf="!imagePath; else imagePreview">
                    <app-file-upload
                      #uploadComponent
                      [uploadType]="uploadType"
                      [style]="uploadStyle"
                      (outputFiles)="updateImage($event)" />
                  </ng-container>
                  <ng-template #imagePreview>
                    <div>
                      <img
                        src="{{ imagePath }}"
                        alt="Session Image"
                        class="session-image" />
                      <button
                        (click)="deleteImage()"
                        aria-label="Delete image"
                        class="btn btn-delete"
                        type="button"
                        [autoClose]="'outside'"
                        ngbTooltip="Delete Image">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </ng-template>
                </div>
                <div class="mb-3">
                  <label class="form-label">Registration Window</label>
                  <div class="row align-items-center">
                    <div class="col">
                      <app-datepicker
                        (dateSelectedUnixTimestamp)="
                          updateRegistrationFromDate($event)
                        "
                        [initialDateUnixTimestamp]="
                          payload.registration_start_datetime ||
                          firstDayOfSchoolYear
                        "
                        [timezone]="defaultTimezone"></app-datepicker>
                    </div>
                    <div class="col-2">
                      <div class="text-center text-nowrap">to</div>
                    </div>
                    <div class="col">
                      <app-datepicker
                        (dateSelectedUnixTimestamp)="
                          updateRegistrationToDate($event)
                        "
                        [initialDateUnixTimestamp]="
                          payload.registration_end_datetime ||
                          lastDayOfSchoolYear
                        "
                        [timezone]="defaultTimezone"></app-datepicker>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-12 col-lg pe-lg-4">
                <div
                  class="fs-22 fw-bold mt-3 w-100 d-flex align-items-center justify-content-between">
                  Microlearning Content
                  <i
                    class="bi bi-exclamation-circle text-danger fs-13 me-2"
                    style="-webkit-text-stroke: 0.4px"
                    *ngIf="contentFormSubmitted && !hasCourseContent"></i>
                </div>
                <div
                  *ngIf="contentFormSubmitted && !hasCourseContent"
                  class="text-danger">
                  Please choose from our course library, upload your own course
                  file, or add content for your course.
                </div>
                <ng-container *ngIf="lmsCourseFileList.length === 0">
                  <label for="lms-course-select" class="form-label"
                    >Embed Course Content
                    <span class="fw-normal">(Optional)</span>
                  </label>
                  <div class="mb-2">
                    Embed course content files from other LMSs here. Choose from
                    our course library, or upload your own course file.
                  </div>
                  <ng-select
                    [labelForId]="'lms-course-select'"
                    [items]="lmsCourses"
                    bindLabel="title"
                    bindValue="id"
                    formControlName="lmsCourse"
                    placeholder="Choose from our course library"></ng-select>
                  <ng-container *ngIf="!f['lmsCourse'].value">
                    <label for="lms-course-upload" class="form-label mt-3 mb-2"
                      >Upload your own course file
                    </label>
                    <app-file-upload
                      [disabled]="lmsCourseIsUploading"
                      [uploadType]="uploadType"
                      [style]="uploadStyle"
                      [multiple]="false"
                      (outputFiles)="uploadLmsCourse($event)" />
                  </ng-container>
                </ng-container>
                <ng-container *ngIf="lmsCourseIsUploading">
                  <div class="fw-600 mt-2">
                    <span
                      *ngIf="lmsCourseIsUploading"
                      class="spinner-border spinner-border-sm me-1 color-tertiary"></span>
                    Uploading your course file, please wait a moment<span
                      class="ellipsis-anim ms-1"
                      ><span>.</span><span>.</span><span>.</span></span
                    >
                  </div>
                </ng-container>
                <app-lms-course-file-list
                  *ngIf="lmsCourseFileList.length > 0"
                  [fileList]="lmsCourseFileList"
                  (deletedFile)="deleteLmsCourseFile($event)" />
                <ng-container *ngIf="lmsErrorMessage && !f['lmsCourse'].value">
                  <div
                    class="alert alert-danger text-start d-flex justify-content-between mt-3 mb-0"
                    role="alert">
                    {{ lmsErrorMessage }}
                    <button
                      type="button"
                      class="btn-close"
                      (click)="lmsErrorMessage = ''"
                      data-bs-dismiss="alert"
                      aria-label="Close"></button>
                  </div>
                </ng-container>
              </div>
              <div class="col ps-lg-4"></div>
            </div>
            <div class="mb-3 mt-4">
              <div class="mb-3" style="max-width: 600px">
                Add content for your course. This can be the full course
                content, or additional information for the embedded course.
              </div>
              <app-wysiwyg-editor
                #courseContentEditor
                (editorContentEvent)="validateCourseNotes($event)"
                [editorContent]="payload.notes"
                [maxHeight]="600"
                [version]="'full'"></app-wysiwyg-editor>
            </div>
          </ng-container>

          <!-- QUIZ SCREEN -->
          <ng-container *ngIf="currentScreen === 'quiz'">
            <div
              class="d-flex justify-content-between align-items-end position-relative">
              <button
                type="button"
                class="btn btn-primary fw-bold"
                (click)="currentScreen = 'content'">
                <i class="bi bi-chevron-left"></i>
                Course Content
              </button>
              <h2
                class="fs-25 fw-bold mb-0 position-absolute bottom-0 start-50 translate-middle-x position-absolute bottom-0 start-50 translate-middle-x">
                Quiz
              </h2>
              <button
                type="button"
                class="btn btn-primary fw-bold"
                (click)="currentScreen = 'eventDetails'">
                Event Details <i class="bi bi-chevron-right ms-1"></i>
              </button>
            </div>
            <hr class="border border-tertiary border-1 mb-4" />

            <ng-container *ngIf="quizId || copiedQuiz">
              <app-microlearning-quiz-builder
                [formId]="copiedQuiz ? copiedQuiz.id : quizId" />
            </ng-container>
          </ng-container>

          <!-- EVENT DETAILS SCREEN -->
          <ng-container *ngIf="currentScreen === 'eventDetails'">
            <div
              class="d-flex justify-content-between align-items-end position-relative">
              <button
                type="button"
                class="btn btn-primary fw-bold"
                (click)="currentScreen = 'quiz'">
                <i class="bi bi-chevron-left"></i>
                Quiz
              </button>
              <h2
                class="fs-25 fw-bold mb-0 position-absolute bottom-0 start-50 translate-middle-x">
                Event Details
              </h2>
              <button
                [disabled]="eventDetailsSubmitLoading"
                (click)="publishCourse()"
                type="button"
                class="btn btn-primary fw-bold">
                <span
                  *ngIf="eventDetailsSubmitLoading"
                  class="spinner-border spinner-border-sm me-1"></span>
                Publish Course
              </button>
            </div>
            <hr class="border border-tertiary border-1 mb-4" />
            <h3 class="fs-19 mt-2 mb-0 fw-bold">Credits</h3>
            <hr class="border border-tertiary border-1" />
            <div class="row mb-5 flex-column flex-lg-row">
              <div class="col pe-lg-4">
                <ng-container *ngIf="settings">
                  <ng-container
                    *ngIf="
                      settings.credits?.length === 0;
                      else addCreditsTemplate
                    ">
                    <p>
                      No credits are available for your district. Please contact
                      your district operator.
                    </p>
                  </ng-container>
                  <ng-template #addCreditsTemplate>
                    <ng-container
                      formArrayName="credits"
                      *ngFor="let credit of credits.controls; let i = index">
                      <div class="d-flex mb-2">
                        <ng-container [formGroupName]="i">
                          <div class="col-8">
                            <ng-select
                              #creditTypeSelect
                              formControlName="creditType"
                              id="credit-type"
                              autocomplete="off"
                              [clearable]="false"
                              [placeholder]="'Select a credit type'"
                              [ngClass]="{
                                'is-invalid':
                                  eventDetailsFormSubmitted &&
                                  credit.get('creditType')?.errors
                              }">
                              <ng-container
                                *ngFor="let credit of settings.credits">
                                <ng-option
                                  [value]="credit.title"
                                  [disabled]="
                                    isCreditTypeSelected(credit.title, i)
                                  "
                                  >{{ credit.title }}</ng-option
                                >
                              </ng-container>
                            </ng-select>
                          </div>
                          <div class="col-3 ps-2">
                            <input
                              formControlName="creditAmount"
                              type="number"
                              class="form-control"
                              id="credit-amount"
                              aria-label="Credit Amount"
                              [ngClass]="{
                                'is-invalid':
                                  eventDetailsFormSubmitted &&
                                  credit.get('creditAmount')?.errors
                              }" />
                          </div>
                          <div
                            class="col-1 d-flex align-items-center justify-content-center">
                            <button
                              (click)="deleteCredit(i)"
                              aria-label="Delete Credit button"
                              class="btn btn-delete btn-sm"
                              type="button"
                              [autoClose]="'outside'"
                              ngbTooltip="Delete Credit">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </ng-container>
                      </div>
                      <div
                        *ngIf="
                          credit.get('creditAmount')?.hasError('nonNegative') &&
                          credit.get('creditAmount')?.touched
                        ">
                        <p class="fs-13 mb-2 text-danger">
                          Credit amount must be above zero.
                        </p>
                      </div>
                      <div
                        *ngIf="
                          credit.get('creditAmount')?.hasError('overLimit')
                        ">
                        <p class="fs-13 mb-2 text-danger">
                          Credit amount has a limit of 99.
                        </p>
                      </div>
                      <div
                        *ngIf="
                          credit.get('creditAmount')?.hasError('invalidDecimal')
                        ">
                        <p class="fs-13 mb-2 text-danger">
                          Credit amount cannot have more than one decimal place
                        </p>
                      </div>
                    </ng-container>
                    <button
                      (click)="addCredit()"
                      type="button"
                      class="btn btn-link fw-bold p-0 text-decoration-none mb-5 mb-lg-0">
                      <i class="bi bi-plus"></i>Create Credit
                    </button>
                  </ng-template>
                </ng-container>
              </div>
              <div class="col col-lg-6 ps-lg-4">
                <label for="credit-award" class="form-label mb-2"
                  >How will credits be awarded?</label
                >
                <ng-select
                  id="credit-award"
                  formControlName="creditAward"
                  [clearable]="false">
                  <ng-option [value]="PdCreditAwardingMethod.SESSION_COMPLETED"
                    >Automatically, when facilitator marks session
                    complete</ng-option
                  >
                  <ng-option [value]="PdCreditAwardingMethod.QUIZ_PASSED"
                    >Automatically, based on quiz results</ng-option
                  >
                  <ng-option [value]="PdCreditAwardingMethod.MANUAL"
                    >Manually, based on facilitator review</ng-option
                  >
                </ng-select>
              </div>
            </div>
            <div class="row mb-5 flex-column flex-lg-row">
              <div class="col pe-lg-4">
                <div class="mb-5" *ngIf="proFormsEnabled">
                  <h3 class="fs-19 mt-2 mb-0 fw-bold">Survey</h3>
                  <hr class="border border-tertiary border-1" />
                  <ng-select
                    #surveySelect
                    formControlName="survey"
                    id="survey"
                    autocomplete="off"
                    [placeholder]="'Select a Survey'"
                    [clearable]="true">
                    <ng-container *ngFor="let form of surveyFormList">
                      <ng-option [value]="form.id">
                        {{ form.title }}
                      </ng-option>
                    </ng-container>
                  </ng-select>
                </div>
                <div class="mb-5">
                  <h3 class="fs-19 mt-2 mb-0 fw-bold">Competency Alignment</h3>
                  <hr class="border border-tertiary border-1" />
                  <app-competency-select
                    #selectCompetency
                    [districtIds]="districtIds"
                    [clearAfterSelection]="false"
                    [isMulti]="true"
                    [defaultCompetencies]="currentCompetencies"
                    (selectedCompetencyList)="addCompetencies($event)"
                    [placeholder]="'Select competency'" />
                </div>
                <div class="mb-5">
                  <h3 class="fs-19 mt-2 mb-0 fw-bold">Categories</h3>
                  <hr class="border border-tertiary border-1" />
                  <ng-container *ngIf="settings">
                    <ng-container
                      *ngIf="
                        settings.categories?.length === 0;
                        else addCategoriesTemplate
                      ">
                      <p>
                        No categories are available for your district. Please
                        contact your district operator.
                      </p>
                    </ng-container>
                    <ng-template #addCategoriesTemplate>
                      <ng-container
                        formArrayName="categories"
                        *ngFor="
                          let category of categories.controls;
                          let i = index
                        ">
                        <div class="d-flex mb-2">
                          <ng-container [formGroupName]="i">
                            <div class="col">
                              <ng-select
                                #categorySelect
                                formControlName="category"
                                id="category"
                                autocomplete="off"
                                [placeholder]="'Select a category'"
                                [clearable]="false"
                                [ngClass]="{
                                  'is-invalid':
                                    eventDetailsFormSubmitted &&
                                    category.get('category')?.errors
                                }">
                                <ng-container
                                  *ngFor="let category of settings.categories">
                                  <ng-option [value]="category.title">
                                    {{ category.title }}
                                  </ng-option>
                                </ng-container>
                              </ng-select>
                            </div>
                            <div class="col ps-2">
                              <ng-select
                                #categoryOptionSelect
                                formControlName="categoryOptions"
                                id="category-options"
                                autocomplete="off"
                                [placeholder]="'Select an option'"
                                [clearable]="false"
                                [ngClass]="{
                                  'is-invalid':
                                    eventDetailsFormSubmitted &&
                                    category.get('categoryOptions')?.errors
                                }">
                                <ng-container
                                  *ngFor="
                                    let option of getFilteredOptions(
                                      categories.controls[i].get('category')
                                        ?.value
                                    )
                                  ">
                                  <ng-option [value]="option.option_value">
                                    {{ option.option_value }}
                                  </ng-option>
                                </ng-container>
                              </ng-select>
                            </div>
                            <div
                              class="col-1 d-flex align-items-center justify-content-center">
                              <button
                                aria-label="Delete Category button"
                                class="btn btn-delete btn-sm"
                                type="button"
                                (click)="deleteCategory(i)"
                                [autoClose]="'outside'"
                                ngbTooltip="Delete Category">
                                <i class="bi bi-trash"></i>
                              </button>
                            </div>
                          </ng-container>
                        </div>
                      </ng-container>
                      <button
                        (click)="addCategory()"
                        type="button"
                        class="btn btn-link fw-bold p-0 text-decoration-none">
                        <i class="bi bi-plus"></i>Create Category
                      </button>
                    </ng-template>
                  </ng-container>
                </div>
                <div *ngIf="isE2L" class="mb-5">
                  <h3 class="fs-19 mt-2 mb-0 fw-bold">Sharing (e2L only)</h3>
                  <hr class="border border-tertiary border-1" />
                  <div class="d-flex align-items-center mb-3">
                    <label class="form-check-label" for="is-shared-switch">
                      Share session across districts?
                    </label>
                    <button
                      aria-label="Share Across Districts"
                      class="form-switch btn-reset ms-2">
                      <input
                        id="is-shared-switch"
                        class="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        formControlName="isShared" />
                    </button>
                  </div>
                  <ng-container *ngIf="f['isShared'].value">
                    <div class="mb-3">
                      <div class="mb-2">
                        Share with these districts (default is share across all
                        districts):
                      </div>
                      <app-district-select
                        #shareWithDistrictSelect
                        [defaultDistricts]="defaultDistrictsSharedWith"
                        (selectedDistrictList)="updateSharing($event, 1)"
                        [isMulti]="true"></app-district-select>
                    </div>
                    <div class="mb-5">
                      <div class="mb-2">Exclude from these districts:</div>
                      <app-district-select
                        #excludeFromDistrictSelect
                        [defaultDistricts]="defaultDistrictsExcludedFrom"
                        (selectedDistrictList)="updateSharing($event, 0)"
                        [isMulti]="true"></app-district-select>
                    </div>
                  </ng-container>
                </div>
              </div>
              <div class="col ps-lg-4">
                <h3 class="fs-19 mt-2 mb-0 fw-bold">Facilitators</h3>
                <hr class="border border-tertiary border-1" />
                <app-user-select
                  [clearAfterSelection]="true"
                  [permission]="'pdFacilitator'"
                  (selectedUser)="addFacilitator($event)"></app-user-select>
                <div class="row mt-2">
                  <ng-container *ngFor="let facilitator of facilitators">
                    <div class="d-flex align-items-center col-6 my-1">
                      <app-avatar
                        [profile]="facilitator.profile"
                        [avatarSize]="'small'"></app-avatar>
                      <span class="ms-2">
                        {{ facilitator.profile.first_name }}
                        {{ facilitator.profile.last_name }}
                      </span>
                      <button
                        (click)="removeFacilitator(facilitator)"
                        aria-label="Remove facilitator button"
                        class="btn btn-delete btn-sm"
                        type="button"
                        ngbTooltip="Remove Facilitator"
                        [autoClose]="'outside'">
                        <i class="bi bi-x-circle-fill"></i>
                      </button>
                    </div>
                  </ng-container>
                </div>
                <ng-container *ngIf="facilitatorInvalid">
                  <p class="text-danger my-2">
                    Please add at least one facilitator.
                  </p>
                </ng-container>
              </div>
            </div>
          </ng-container>
        </form>
      </div>
    </div>
  </div>
</ng-container>
